import {RestService} from "@/services/rest/RestService";
import {OAuth2Token} from "@/dtos/OAuth2Token";
import {generateUUIDv4} from "@/helpers/functions/string";
import {Storage} from "@/models/facade/Storage";
import {SessionStorageKeyEnum} from "@/models/enum/SessionStorageKeyEnum";
import { nanoid } from "nanoid";
import { UtilityHelper } from "@/helpers/UtilityHelper";

export class AuthRestService extends RestService
{
    public async getLoginUrl(): Promise<string>
    {
        const origin: string = window.location.origin;
        const baseUrl: string = this.getConfig().authBaseUrl;
        const clientId: string = this.getConfig().authClientId;
        const state: string = generateUUIDv4();
        const codeVerifier: string = nanoid(32);

        Storage.session.set(
            SessionStorageKeyEnum.AUTH_FLOW_STATE,
            state
        );
        Storage.session.set(
            SessionStorageKeyEnum.AUTH_FLOW_CODE_VERIFIER,
            codeVerifier
        );

        const codeChallenge = await UtilityHelper.hashSHA256(codeVerifier);
        const codeChallengeBase64 =
            UtilityHelper.urlSafeBase64Encode(codeChallenge);

        return `${baseUrl}?client_id=${clientId}&state=${state}&response_type=code&redirect_uri=${origin}/auth/callback&code_challenge_method=S256&code_challenge=${codeChallengeBase64}`;
    }

    public async getLogoutUrl(): Promise<string>
    {
        const baseUrl: string = this.getConfig().authBaseUrl;
        const clientId: string = this.getConfig().authClientId;
        const mgmtUrl: string = this.getConfig().mgmtUrl;

        return baseUrl + "/logout?client_id=" + clientId + "&redirect_uri=" + mgmtUrl;
    }

    public async getTokenByCode(
        code: string,
        state: string,
    ): Promise<OAuth2Token> {
        const sessionState = Storage.session.get(
            SessionStorageKeyEnum.AUTH_FLOW_STATE,
            null
        );

        if (sessionState === null || state !== sessionState) {
            throw new Error("Invalid state param");
        }

        const clientId: string = this.getConfig().authClientId;
        const codeVerifier: string = Storage.session.get(
            SessionStorageKeyEnum.AUTH_FLOW_CODE_VERIFIER,
            ""
        );
        const codeVerifierBase64: string = UtilityHelper.urlSafeBase64Encode(codeVerifier);

        const response = await this.getAxiosInstance().post(
            this.getApiGwUrl("/pharma-auth/admin/v1/login/token"),
            {
                grant_type: "authorization_code",
                client_id: clientId,
                code: code,
                code_verifier: codeVerifierBase64,
            },
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        if (response.status !== 200) {
            throw new Error(response.data);
        }

        return new OAuth2Token(
            response.data["id_token"],
            response.data["refresh_token"],
            3600
        );
    }

    public async refreshToken(userId: string, refreshToken: string): Promise<OAuth2Token> {
        const response = await this.getAxiosInstance().post(
            this.getApiGwUrl("/pharma-auth/admin/v1/login/refresh_token"),
            {
                user_id: userId,
                refresh_token: refreshToken,
            },
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        if (response.status !== 200) {
            throw new Error(response.data);
        }

        return new OAuth2Token(
            response.data["id_token"],
            refreshToken,
            3600
        );
    }
}
